import { assign, first, isEmpty, map, startCase, values } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Input, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import WideModal from '../ReusableComponents/WideModal';
import styles from './Deliveries.module.css';
import Button from '../ReusableComponents/Button/Button';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';

const useStyles = makeStyles()((theme) => ({
  save: {
    color: 'white',
    background: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
}));

const makeMenuItems = (locations) =>
  map(locations, (location) => ({
    value: location.id,
    text: startCase(location.name),
  }));

const getInitialBlock = (block, location_id = null) => {
  if (!location_id) return assign({}, block);
  return assign({}, block, { location_id });
};

export default function BlockModal(props) {
  const { classes } = useStyles();
  const { block: pBlock, locations, onClose, open } = props;

  const [date, setDate] = useState(moment.utc().toDate());
  const [error, setError] = useState(null);
  const [block, setBlock] = useState(getInitialBlock(pBlock));

  const setLocation = ({ target: { value: location_id } }) => {
    setBlock(assign({}, block, { location_id }));
  };

  const setNotes = ({ target: { value: notes } }) => {
    setBlock(assign({}, block, { notes }));
  };

  const setTime = ({ target: { value: time, name } }) => {
    const [hours, minutes] = time.split(':');
    const timeDate = moment
      .utc(block[name])
      .startOf('day')
      .add(hours, 'hours')
      .add(minutes, 'minutes')
      .toDate();

    setBlock(assign({}, block, { [name]: timeDate }));
  };

  const save = () => {
    const { starts_at, ends_at } = block;
    const { saveBlock } = props;
    const mDate = moment.utc(date).startOf('day');
    const startsAt = Math.abs(
      moment
        .utc(starts_at || mDate)
        .startOf('day')
        .diff(moment.utc(starts_at || mDate), 'minutes'),
    );

    const endsAt = Math.abs(
      moment
        .utc(ends_at || mDate)
        .startOf('day')
        .diff(moment.utc(ends_at || mDate), 'minutes'),
    );

    block.starts_at = mDate.clone().add(startsAt, 'minutes').toDate();
    block.ends_at = mDate.clone().add(endsAt, 'minutes').toDate();

    if (
      block.starts_at > block.ends_at ||
      moment.utc(block.starts_at).isSame(moment.utc(block.ends_at))
    ) {
      return setError('The start time must be before the end time');
    }

    if (isEmpty(block.notes)) {
      return setError('Please add a note');
    }

    setError('');
    return saveBlock(block);
  };

  const location = locations[block.location_id] || first(values(locations));

  return (
    <WideModal
      open={open}
      onClose={onClose}
      onRendered={() => setBlock(getInitialBlock(pBlock, location.id))}
    >
      <div className={styles.blockModal}>
        <h3 className={styles.blockModalHeader}>Manage Block:</h3>
        <div className={styles.blockModalDetails}>
          <div className={styles.blockModalDetailsContainer}>
            <SelectInput
              label="Location:"
              onChange={setLocation}
              value={location.id}
              name="location"
              native
              noDefault
              input={<Input name="location" id="block-modal-location-select" />}
              options={makeMenuItems(locations)}
            />

            <div className={styles.date}>
              <TextInput
                id="block-modal-date"
                label="Date"
                type="date"
                fullWidth
                defaultValue={moment.utc(date).format('YYYY-MM-DD')}
                InputLabelProps={{
                  shrink: true,
                }}
                onChange={({ target: { value } }) => setDate(value)}
              />
            </div>
          </div>

          <div className={styles.blockModalDetailsContainer}>
            <TextInput
              id="block-modal-starts-at"
              label="Start Time"
              type="time"
              name="starts_at"
              value={
                block.starts_at
                  ? moment.utc(block.starts_at).format('HH:mm')
                  : '00:00'
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1800 }}
              onChange={setTime}
            />

            <div className={styles.endTime}>
              <TextInput
                id="block-modal-ends-at"
                label="End Time"
                fullWidth
                type="time"
                name="ends_at"
                className={styles.endTime}
                value={
                  block.ends_at
                    ? moment.utc(block.ends_at).format('HH:mm')
                    : '00:00'
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 1800 }}
                onChange={setTime}
              />
            </div>

            <TextField
              label="Notes"
              InputLabelProps={{ shrink: true }}
              id="block-modal-notes"
              value={block.notes || ''}
              margin="normal"
              variant="outlined"
              onChange={setNotes}
            />
          </div>
        </div>
        <div className={styles.error}>{error}</div>
        <div className={styles.blockMessage}>
          Please Note: Creating a block will automatically delete any deliveries
          scheduled during the time of the block
        </div>
        <div className={styles.blockModalActions}>
          <Button
            className={classes.save}
            variant="contained"
            color="primary"
            onClick={save}
            id="save-button"
          >
            Save
          </Button>

          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            id="cancel-button"
          >
            Cancel
          </Button>
        </div>
      </div>
    </WideModal>
  );
}

BlockModal.propTypes = {
  block: PropTypes.shape({}).isRequired,
  locations: PropTypes.shape({}).isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  saveBlock: PropTypes.func.isRequired,
};
